// Запросы и действия табеля.
//
// Правило 11 про время: прошлый срез из кэша показывается сразу, свежий подменяет его без
// спиннера поверх уже показанного числа. Табельщик правит ячейку за ячейкой, и мигание сетки
// после каждой правки сделало бы работу невозможной.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '@/shared/errors/api-error';
import type { BulkFactIn, BulkFactOut, FactIn, FactOut } from '@/shared/api/endpoints/payouts';
import { markPaid, markPaidBulk, reverseFact } from '@/shared/api/endpoints/payouts';
import { countLabel } from '@/shared/text/plural';
import { подтверждение } from '@/shared/ui/toast/подтверждение';
import type {
  DebtOut,
  DebtsQuery,
  LossesReportOut,
  OpeningBalancesCancelOut,
  OpeningBalancesIn,
  OpeningBalancesPlanOut,
  RepaymentOut,
} from '@/shared/api/endpoints/losses';
import type { DebtScheduleIn } from '@/shared/api/endpoints/losses';
import {
  cancelOpeningBalances,
  fetchDebts,
  fetchLosses,
  fetchRepayments,
  planOpeningBalances,
  setDebtSchedule,
} from '@/shared/api/endpoints/losses';
import type {
  DeductionsReportOut,
  DebtsReportOut,
  PayoutRegisterOut,
  PayrollSummaryOut,
  PayrollTrendOut,
  ReportScope,
  StaffingOut,
} from '@/shared/api/endpoints/reports';
import {
  fetchDeductions,
  fetchDebtsReport,
  fetchPayoutRegister,
  fetchPayrollSummary,
  fetchPayrollTrend,
  fetchStaffing,
} from '@/shared/api/endpoints/reports';

import {
  closePeriod,
  copyDay,
  fetchEmployeeMonths,
  fetchGrid,
  fetchPeriods,
  fillByPlan,
  generateSchedule,
  reopenPeriod,
  setCell,
  setCells,
  setEntryMode,
  setMonthTotals,
  setShiftType,
  setTimesheetNote,
  setTimesheetStatus,
  type CellIn,
  type CopyDayOut,
  type EmployeeMonthOut,
  type EntryMode,
  type FillOut,
  type GenerateOut,
  type GridOut,
  type TimesheetNoteIn,
  type MonthTotalIn,
  type PeriodOut,
  type ShiftTypeIn,
} from '@/shared/api/endpoints/timesheet';
import type { ListResponse } from '@/shared/api/types';
import type {
  BulkExtendIn,
  BulkExtendOut,
  OnShiftUnassignedListOut,
} from '@/shared/api/endpoints/people';
import { extendAssignmentsBulk, fetchOnShiftUnassigned } from '@/shared/api/endpoints/people';
import type { Period } from '@/shared/time/period';

export const timesheetKeys = {
  все: ['timesheet'] as const,
  периоды: (период: Period, q: string | undefined) =>
    ['timesheet', 'periods', период.year, период.month, q ?? ''] as const,
  сетка: (siteId: string, период: Period) =>
    ['timesheet', 'grid', siteId, период.year, период.month] as const,
  месяцыЧеловека: (employeeId: string) => ['timesheet', 'employee-months', employeeId] as const,
  /**
   * «На вахте, но не на посту». Под корнем табеля намеренно: продление сбрасывает табель
   * целиком (строки появляются в сетке), и список уходит вместе с ним одной командой.
   */
  безПоста: (siteId: string | null) =>
    ['timesheet', 'on-shift-unassigned', siteId ?? 'all'] as const,
};

/**
 * Люди «на вахте» без действующего и будущего назначения: по объекту последнего назначения
 * либо все (`null`). Список открыт только праву правки людей — `enabled` выключает запрос, чтобы
 * роль без права не получала отказ на каждом открытии табеля.
 */
export function useOnShiftUnassigned(
  siteId: string | null,
  enabled = true,
): UseQueryResult<OnShiftUnassignedListOut, ApiError> {
  return useQuery<OnShiftUnassignedListOut, ApiError>({
    queryKey: timesheetKeys.безПоста(siteId),
    queryFn: ({ signal }) => fetchOnShiftUnassigned({ siteId }, signal),
    enabled,
  });
}

/**
 * Продление пачкой. Подтверждения НЕТ намеренно: панель не закрывается и сама держит итог с
 * пропущенными поимённо — всплывающее «Назначения продлены» поверх него сказало бы то же
 * короче и мимо пропущенных.
 *
 * Сбрасывает табель (строки продлённых появляются в сетке, список пустеет), людей (карточки),
 * справочники (укомплектованность объектов) и дела. Ключи чужих разделов — буквально: разделы
 * за кодом друг к другу не ходят.
 */
export function useExtendAssignmentsBulk(): UseMutationResult<
  BulkExtendOut,
  ApiError,
  BulkExtendIn
> {
  const client = useQueryClient();
  return useMutation<BulkExtendOut, ApiError, BulkExtendIn>({
    mutationFn: (body) => extendAssignmentsBulk(body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: timesheetKeys.все });
      void client.invalidateQueries({ queryKey: ['people'] });
      void client.invalidateQueries({ queryKey: ['org'] });
      void client.invalidateQueries({ queryKey: ['cases'] });
    },
  });
}

export function usePeriods(период: Period, q?: string): UseQueryResult<ListResponse<PeriodOut>> {
  return useQuery({
    queryKey: timesheetKeys.периоды(период, q),
    queryFn: () => fetchPeriods(период, q === undefined ? {} : { q }),
    // Прошлый месяц остаётся на экране, пока едет следующий: список не должен мигать пустотой.
    placeholderData: (прежние) => прежние,
  });
}

/**
 * Часы человека по месяцам для его карточки.
 *
 * Живёт здесь, рядом с остальным табелем, а не в запросах людей: это данные табеля, и право на
 * них то же самое. Второй ключ кэша на те же строки означал бы, что закрытый месяц обновится в
 * одном месте и останется старым в другом.
 */
export function useEmployeeMonths(
  employeeId: string | null,
): UseQueryResult<ListResponse<EmployeeMonthOut>> {
  return useQuery({
    queryKey: timesheetKeys.месяцыЧеловека(employeeId ?? 'нет'),
    queryFn: () => fetchEmployeeMonths(employeeId!),
    enabled: employeeId !== null,
  });
}

export function useGrid(siteId: string | null, период: Period): UseQueryResult<GridOut> {
  return useQuery({
    queryKey: timesheetKeys.сетка(siteId ?? 'нет', период),
    queryFn: () => fetchGrid(siteId!, период),
    enabled: siteId !== null,
    placeholderData: (прежние) => прежние,
  });
}

/**
 * Сброс после действия. Табель кормит ведомость и пульт: часы меняют и «вышло» по объекту, и
 * будущее начисление, поэтому гасится всё дерево запросов раздела, а не одна сетка.
 */
function useСброс() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: timesheetKeys.все });
  };
}

/** Свежая сетка кладётся в кэш прямо из ответа: лишний запрос после каждой правки не нужен. */
function useПоложитьСетку() {
  const client = useQueryClient();
  return (siteId: string, период: Period, сетка: GridOut) => {
    client.setQueryData(timesheetKeys.сетка(siteId, период), сетка);
    void client.invalidateQueries({ queryKey: ['timesheet', 'periods'] });
  };
}

export function useSetCell(siteId: string, период: Period) {
  const положить = useПоложитьСетку();
  return useMutation<GridOut, unknown, CellIn>({
    // МОЛЧИТ намеренно: число появилось в клетке, итог по объекту пересчитался тут же.
    // Всплывающая подсказка на каждое нажатие в сетке на 281 строку — шум хуже молчания.
    mutationFn: (body) => setCell(siteId, период, body),
    onSuccess: (сетка) => положить(siteId, период, сетка),
  });
}

export function useSetCells(siteId: string, период: Period) {
  const положить = useПоложитьСетку();
  return useMutation<GridOut, unknown, CellIn[]>({
    mutationFn: (cells) => setCells(siteId, период, cells),
    // Правка ОДНОЙ ячейки молчит намеренно: число появилось в клетке, итог пересчитался, и
    // всплывающая подсказка на каждое нажатие в сетке на 281 строку — шум хуже молчания.
    // Массовая отметка другая: человек нажал один раз, а изменилось много, и сколько именно —
    // с экрана не сосчитать.
    meta: подтверждение<CellIn[]>((ячейки) =>
      countLabel(ячейки.length, ['смена отмечена', 'смены отмечены', 'смен отмечено']),
    ),
    onSuccess: (сетка) => положить(siteId, период, сетка),
  });
}

export function useSetMonthTotals(siteId: string, период: Period) {
  const положить = useПоложитьСетку();
  return useMutation<GridOut, unknown, MonthTotalIn[]>({
    mutationFn: (body) => setMonthTotals(siteId, период, body),
    meta: подтверждение<MonthTotalIn[]>((строки) =>
      countLabel(строки.length, ['итог записан', 'итога записано', 'итогов записано']),
    ),
    onSuccess: (сетка) => положить(siteId, период, сетка),
  });
}

export function useSetEntryMode(siteId: string, период: Period) {
  const положить = useПоложитьСетку();
  return useMutation<GridOut, unknown, EntryMode>({
    // МОЛЧИТ намеренно: сама сетка перестраивается на глазах, а переключатель показывает
    // выбранное. Подтверждать переключатель — всё равно что подтверждать нажатие кнопки.
    mutationFn: (mode) => setEntryMode(siteId, период, mode),
    onSuccess: (сетка) => положить(siteId, период, сетка),
  });
}

/**
 * Тип смены на всю строку месяца: день, ночь, сутки.
 *
 * Молчит, как и переключатель способа ведения: выбранное видно в самой строке, а
 * подтверждение выбора — это подтверждение нажатия кнопки.
 */
export function useSetShiftType(siteId: string, период: Period) {
  const положить = useПоложитьСетку();
  return useMutation<GridOut, unknown, ShiftTypeIn>({
    // МОЛЧИТ намеренно, как и переключатель способа ведения: выбранный тип тут же виден в
    // самой строке, а подтверждать то, что видно на экране, — значит подтверждать нажатие.
    mutationFn: (тело) => setShiftType(siteId, период, тело),
    onSuccess: (сетка) => положить(siteId, период, сетка),
  });
}

/** Повторить день до конца месяца. Уже отмеченное не перезаписывается. */
export function useCopyDay(siteId: string, период: Period) {
  const сбросить = useСброс();
  return useMutation<CopyDayOut, unknown, string | undefined>({
    // МОЛЧИТ намеренно: ответ несёт числа («повторили 5 сентября, заполнено 26 по 7 людям,
    // пропущено 3»), и экран показывает их строкой над сеткой. Всплывающее подтверждение
    // рядом с этой строкой сказало бы то же самое вторым голосом и короче.
    mutationFn: (день) => copyDay(siteId, период, день),
    onSuccess: () => сбросить(),
  });
}

/**
 * Черновик ↔ на проверке.
 *
 * Закрытие сюда не входит: у него своё право и своя отмена через переоткрытие. «На проверке»
 * правки ещё принимает — иначе оно ничем не отличалось бы от закрытия.
 */
export function useSetTimesheetStatus(siteId: string, период: Period) {
  const положить = useПоложитьСетку();
  return useMutation<GridOut, unknown, { status: 'draft' | 'review'; version: number }>({
    // МОЛЧИТ намеренно: состояние написано на самой кнопке — она становится «Вернуть в
    // работу». Действие обратимо тем же нажатием, и объяснять тут нечего.
    mutationFn: ({ status, version }) => setTimesheetStatus(siteId, период, status, version),
    onSuccess: (сетка) => положить(siteId, период, сетка),
  });
}

export function useSetTimesheetNote(siteId: string, период: Period) {
  const положить = useПоложитьСетку();
  return useMutation<GridOut, unknown, TimesheetNoteIn>({
    // МОЛЧИТ намеренно: итог виден там же, где действие, — под полем появляется «Сохранено
    // 21.09.2026 в 14:32, Петров». Всплывающее подтверждение сказало бы то же самое вторым
    // голосом и уехало бы, а подпись остаётся и через месяц отвечает на «кто это писал».
    mutationFn: (body) => setTimesheetNote(siteId, период, body),
    onSuccess: (сетка) => положить(siteId, период, сетка),
  });
}

export function useGenerate(siteId: string, период: Period) {
  const сбросить = useСброс();
  return useMutation<GenerateOut, unknown, void>({
    mutationFn: () => generateSchedule(siteId, период),
    // Число обязательно: «график построен» на пустом объекте и на объекте с шестьюдесятью
    // сменами выглядит одинаково, а разница между ними — рабочий день табельщика.
    meta: подтверждение<void, GenerateOut>((_ввод, итог) =>
      countLabel(итог.created, ['смена в графике', 'смены в графике', 'смен в графике']),
    ),
    onSuccess: сбросить,
  });
}

export function useFillByPlan(siteId: string, период: Period) {
  const сбросить = useСброс();
  return useMutation<FillOut, unknown, void>({
    mutationFn: () => fillByPlan(siteId, период),
    meta: подтверждение<void, FillOut>((_ввод, итог) =>
      countLabel(итог.filled, ['смена отмечена', 'смены отмечены', 'смен отмечено']),
    ),
    onSuccess: сбросить,
  });
}

export function useClosePeriod(siteId: string, период: Period) {
  const положить = useПоложитьСетку();
  return useMutation<GridOut, unknown, number>({
    mutationFn: (version) => closePeriod(siteId, период, version),
    meta: подтверждение('Табель закрыт', 'Правка ячеек больше не принимается'),
    onSuccess: (сетка) => положить(siteId, период, сетка),
  });
}

export function useReopenPeriod(siteId: string, период: Period) {
  const положить = useПоложитьСетку();
  return useMutation<GridOut, unknown, { version: number; basis: string }>({
    mutationFn: ({ version, basis }) => reopenPeriod(siteId, период, version, basis),
    meta: подтверждение('Табель открыт заново', 'Основание записано в журнал'),
    onSuccess: (сетка) => положить(siteId, период, сетка),
  });
}

// ── Реестр долгов (тз, раздел 5.5) ─────────────────────────────────────────────────────────

/**
 * Долги с остатками по всей организации.
 *
 * Не то же, что долги ОДНОГО человека в его карточке: там вопрос «сколько на нём», здесь —
 * «сколько на всех и по каким типам». Общий ключ кэша у них разный намеренно: карточка
 * обновляется после выдачи формы, реестр — после утверждения ведомости.
 */
export function useDebtsRegistry(
  отбор: DebtsQuery = {},
): UseQueryResult<ListResponse<DebtOut>, ApiError> {
  return useQuery<ListResponse<DebtOut>, ApiError>({
    queryKey: [
      'payroll',
      'debts-registry',
      отбор.legal_entity_id ?? null,
      отбор.debt_type ?? null,
      отбор.status ?? null,
      отбор.only_active ?? false,
    ],
    queryFn: () => fetchDebts(отбор),
    placeholderData: (прежние) => прежние,
  });
}

/**
 * Задать или поменять график погашения долга (тз, раздел 5.5).
 *
 * Сумму долга не трогает — только то, как её забирают. Обновляет ОБА списка: реестр долгов в
 * «Расчёте» и вкладку «Долги» в карточке человека. Одного мало: долг открывают из реестра, а
 * проверять идут в карточку, и устаревшая фраза про график там читается как «правка не
 * сохранилась», после чего её делают второй раз.
 */
export function useSetDebtSchedule(
  employeeId?: string,
): UseMutationResult<DebtOut, ApiError, { debtId: string; body: DebtScheduleIn }> {
  const client = useQueryClient();
  return useMutation<DebtOut, ApiError, { debtId: string; body: DebtScheduleIn }>({
    mutationFn: ({ debtId, body }) => setDebtSchedule(debtId, body),
    // Подтверждение словами, а не «сохранено»: график решает, сколько человек получит на руки
    // в ближайшие месяцы, и увидеть новое условие целиком важнее, чем факт записи.
    meta: подтверждение<{ debtId: string; body: DebtScheduleIn }, DebtOut>(
      (_ввод, итог) => `Новый график: ${итог.schedule_note}`,
    ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['payroll'] });
      void client.invalidateQueries({ queryKey: ['people', 'debts'] });
      if (employeeId !== undefined) {
        void client.invalidateQueries({ queryKey: ['people', 'final-settlement', employeeId] });
      }
    },
  });
}

/**
 * После записи переноса остатков долги меняются везде разом: реестр и сводка «Расчёта»,
 * вкладка «Долги» у людей и отметка «есть долг» в их списке, плитка первого экрана, «Дела» и
 * сам признак «перенесены ли остатки». Обновляем все эти места — иначе человек, перенёсший
 * лист, откроет карточку и увидит прежнее «долгов нет».
 */
function обновитьПослеПереноса(client: ReturnType<typeof useQueryClient>): void {
  for (const ключ of [['payroll'], ['people'], ['debts'], ['money'], ['cases']]) {
    void client.invalidateQueries({ queryKey: ключ });
  }
}

/** Перенос остатков листом: показать, что понято (`dry_run`), либо записать. */
export function usePlanOpeningBalances(): UseMutationResult<
  OpeningBalancesPlanOut,
  ApiError,
  OpeningBalancesIn
> {
  const client = useQueryClient();
  return useMutation<OpeningBalancesPlanOut, ApiError, OpeningBalancesIn>({
    // МОЛЧИТ намеренно: итог словами — сколько, на сколько, что не перенеслось и почему —
    // рисует сама панель переноса. Всплывающее «готово» поверх отчёта повторило бы его хуже, а
    // на показе листа (без записи) и вовсе соврало бы, что что-то сделано.
    mutationFn: (body) => planOpeningBalances(body),
    onSuccess: (итог) => {
      if (!итог.dry_run) {
        обновитьПослеПереноса(client);
      }
    },
  });
}

/** Отмена переноса остатков целиком: показать, что отменится, либо отменить. */
export function useCancelOpeningBalances(): UseMutationResult<
  OpeningBalancesCancelOut,
  ApiError,
  { basis: string; dry_run: boolean }
> {
  const client = useQueryClient();
  return useMutation<OpeningBalancesCancelOut, ApiError, { basis: string; dry_run: boolean }>({
    // МОЛЧИТ намеренно: что отменится и что остаётся, панель показывает сама, с суммой.
    mutationFn: (body) => cancelOpeningBalances(body),
    onSuccess: (итог) => {
      if (!итог.dry_run) {
        обновитьПослеПереноса(client);
      }
    },
  });
}

/** История удержаний по одному долгу: карточка долга (тз, раздел 5.5). */
export function useRepayments(
  debtId: string,
): UseQueryResult<ListResponse<RepaymentOut>, ApiError> {
  return useQuery<ListResponse<RepaymentOut>, ApiError>({
    queryKey: ['payroll', 'repayments', debtId],
    queryFn: () => fetchRepayments(debtId),
  });
}

// ── Панели показателей (сценарии 60, 61) ───────────────────────────────────────────────────

/**
 * Ключ кэша по отбору. Отбор — часть вопроса, а не украшение: два отчёта с разным отбором это
 * два разных ответа, и класть их под один ключ значит показать чужие числа.
 */
function ключОтбора(отбор: ReportScope): (string | null)[] {
  return [отбор.legal_entity_id ?? null, отбор.customer_id ?? null, отбор.site_id ?? null];
}

/**
 * `включено` есть у всех пяти отчётов сводки, и это не украшение.
 *
 * Права на пять блоков сводки РАЗНЫЕ, а вкладка одна. Табельщик и старший объекта видят её по
 * праву на панели показателей, но ведомостей и потерь им не положено — и до 06.09.2026 экран
 * всё равно посылал все пять запросов и выдавал им три красных блока «не удалось загрузить»
 * из пяти. Красная ошибка означает поломку; здесь же всё работало правильно, просто данные не
 * их. Запрос без права не отправляется вовсе, а блок не рисуется: человек видит свой экран
 * целиком, а не чужой наполовину.
 */
export function useStaffing(
  отбор: ReportScope & { year?: number; month?: number } = {},
  включено = true,
): UseQueryResult<StaffingOut, ApiError> {
  return useQuery<StaffingOut, ApiError>({
    // Месяц входит в ключ: без него переключение периода отдавало бы часы прошлого месяца из
    // кэша, а числа на экране менялись бы только при обновлении страницы.
    queryKey: [
      'payroll',
      'staffing',
      отбор.year ?? null,
      отбор.month ?? null,
      ...ключОтбора(отбор),
    ],
    queryFn: () => fetchStaffing(отбор),
    enabled: включено,
    // Прошлый срез держится на экране, пока едет новый: правило 11, спиннер на месте уже
    // показанного числа читается как потеря данных.
    placeholderData: (прежние) => прежние,
  });
}

export function usePayrollSummary(
  year: number,
  month: number,
  отбор: ReportScope = {},
  включено = true,
): UseQueryResult<PayrollSummaryOut, ApiError> {
  return useQuery<PayrollSummaryOut, ApiError>({
    queryKey: ['payroll', 'summary', year, month, ...ключОтбора(отбор)],
    queryFn: () => fetchPayrollSummary({ year, month, ...отбор }),
    enabled: включено,
    placeholderData: (прежние) => прежние,
  });
}

/**
 * Стоимость месяца за последние двенадцать месяцев.
 *
 * Отдельный запрос от сводки, а не расширение её: сводка отвечает на «сколько стоит ЭТОТ
 * месяц» и меняется при каждом отборе, а ряд — на «растёт или падает» и стоит на экране,
 * пока листают месяцы. Один запрос на два вопроса пришлось бы переспрашивать вдвое чаще.
 */
export function usePayrollTrend(
  year: number,
  month: number,
  отбор: ReportScope = {},
  включено = true,
): UseQueryResult<PayrollTrendOut, ApiError> {
  return useQuery<PayrollTrendOut, ApiError>({
    queryKey: ['payroll', 'trend', year, month, ...ключОтбора(отбор)],
    queryFn: () => fetchPayrollTrend({ year, month, months: 12, ...отбор }),
    enabled: включено,
    placeholderData: (прежние) => прежние,
  });
}

/**
 * Реестр выплат за период (тз, раздел 5.7): кому и сколько переводить.
 *
 * Отдельный от сводки запрос: сводка отвечает на «сколько стоит месяц», реестр — на «кому
 * перевести сегодня». Это разные вопросы и разные документы.
 */
export function usePayoutRegister(
  year: number,
  month: number,
  отбор: ReportScope = {},
  включено = true,
): UseQueryResult<PayoutRegisterOut, ApiError> {
  return useQuery<PayoutRegisterOut, ApiError>({
    queryKey: ['payroll', 'payout-register', year, month, ...ключОтбора(отбор)],
    queryFn: () => fetchPayoutRegister({ year, month, ...отбор }),
    enabled: включено,
    placeholderData: (прежние) => прежние,
  });
}

export function useDebtsReport(
  year: number,
  month: number,
  legalEntityId?: string,
  включено = true,
): UseQueryResult<DebtsReportOut, ApiError> {
  return useQuery<DebtsReportOut, ApiError>({
    queryKey: ['payroll', 'debts', year, month, legalEntityId ?? null],
    queryFn: () => fetchDebtsReport({ year, month, legal_entity_id: legalEntityId }),
    enabled: включено,
    // Прошлый срез остаётся на экране, пока едет новый: правило 11.
    placeholderData: (прежние) => прежние,
  });
}

export function useLossesReport(
  year: number,
  month: number,
  включено = true,
): UseQueryResult<LossesReportOut, ApiError> {
  return useQuery<LossesReportOut, ApiError>({
    queryKey: ['payroll', 'losses', year, month],
    queryFn: () => fetchLosses({ year, month }),
    enabled: включено,
    placeholderData: (прежние) => прежние,
  });
}

/**
 * Отметка «выплачено» по расчёту человека (сценарии 37 и 76).
 *
 * Сбрасывает сетку целиком: факт выплаты виден и в колонке реквизитов, и в состоянии
 * ведомости. Оптимистичного обновления нет намеренно — «выплачено» до ответа сервера было бы
 * неправдой о деньгах.
 */
/**
 * Отметить выплаченной всю ведомость (тз, раздел 5.4.6).
 *
 * Сбрасывает сетку целиком: факт выплаты виден в колонке реквизитов у каждой строки.
 */
export function useMarkPaidBulk(): UseMutationResult<BulkFactOut, ApiError, BulkFactIn> {
  const client = useQueryClient();
  return useMutation<BulkFactOut, ApiError, BulkFactIn>({
    mutationFn: (body) => markPaidBulk(body),
    // Отметка выплаты необратима на глаз: человек уходит из списка «кому перевести», и
    // вернуть его туда можно только отменой факта. Молчание тут читается как «не сработало»,
    // и отметку ставят второй раз — на 281 строке это минута работы впустую и сомнение в
    // числах, которые на самом деле верны.
    meta: подтверждение<BulkFactIn, BulkFactOut>(
      (_ввод, итог) =>
        countLabel(итог.created, ['выплата отмечена', 'выплаты отмечены', 'выплат отмечено']),
      'на сумму, которую видно в полосе над сеткой',
    ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['payroll'] });
      void client.invalidateQueries({ queryKey: ['people'] });
    },
    // Отказ по состоянию (409: ведомость уже отмечена или её вернули в работу) перечитывает
    // ведомость сразу, иначе полминуты на экране стоит прежнее состояние с живой кнопкой.
    onError: (ошибка) => {
      if (ошибка.status === 409) {
        void client.invalidateQueries({ queryKey: ['payroll'] });
      }
    },
  });
}

export function useReverseFact(): UseMutationResult<
  FactOut,
  ApiError,
  { factId: string; basis: string }
> {
  const client = useQueryClient();
  return useMutation<FactOut, ApiError, { factId: string; basis: string }>({
    mutationFn: ({ factId, basis }) => reverseFact(factId, basis),
    meta: подтверждение('Выплата отменена', 'человек снова в списке «кому перевести»'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['payroll'] });
      void client.invalidateQueries({ queryKey: ['people'] });
      void client.invalidateQueries({ queryKey: ['payouts'] });
    },
  });
}

export function useMarkPaid(): UseMutationResult<
  FactOut,
  ApiError,
  FactIn & { idempotencyKey: string }
> {
  const client = useQueryClient();
  return useMutation<FactOut, ApiError, FactIn & { idempotencyKey: string }>({
    mutationFn: ({ idempotencyKey, ...body }) => markPaid(body, idempotencyKey),
    // Подробности «человек ушёл из списка «кому перевести»» нет намеренно: отметка бывает
    // частичной и бывает доплатой, и тогда человек в списке остаётся. Ответ сервера (`FactOut`)
    // про остаток не говорит, а считать его здесь по кэшу значило бы завести второй расчёт.
    meta: подтверждение('Выплата отмечена'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['payroll'] });
      void client.invalidateQueries({ queryKey: ['people'] });
    },
    // Отказ по состоянию (409: ведомость уже отмечена или её вернули в работу) перечитывает
    // ведомость сразу, иначе полминуты на экране стоит прежнее состояние с живой кнопкой.
    onError: (ошибка) => {
      if (ошибка.status === 409) {
        void client.invalidateQueries({ queryKey: ['payroll'] });
      }
    },
  });
}

/**
 * Удержания за месяц: кому сколько аванса, билетов, формы, обучения.
 *
 * Ключ в пространстве «payroll»: пересчёт и утверждение ведомостей сбрасывают его вместе с
 * остальным разделом, и вкладка не показывает удержания прошлого расчёта.
 */
export function useDeductions(
  период: Period,
  legalEntityId: string | undefined,
): UseQueryResult<DeductionsReportOut, ApiError> {
  return useQuery<DeductionsReportOut, ApiError>({
    queryKey: ['payroll', 'deductions', период.year, период.month, legalEntityId ?? null],
    queryFn: () =>
      fetchDeductions({ year: период.year, month: период.month, legal_entity_id: legalEntityId }),
    placeholderData: (прежние) => прежние,
  });
}
